import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QRCode from 'qrcode';

import { useTranslation } from 'react-i18next';

import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import { selectors } from '../../reducer';
import * as actions from '../../actions';
import * as Styled from './ApplyRecipeInfo.styles';
import { rxGateDistributeClientBonus } from '../../helpers';

const ApplyRecipeInfo: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const provisionStatus = useSelector(selectors.provisionStatus);
  const provisionStatusError = useSelector(selectors.provisionStatusError);
  const provisionStatusLoading = useSelector(selectors.provisionStatusLoading);

  const rxGateChosenReceipt = useSelector(selectors.rxGateChosenReceipt);

  const applyRecipeResult = useSelector(selectors.applyRecipeResult);
  const applyRecipeLoading = useSelector(selectors.applyRecipeLoading);
  const applyRecipeError = useSelector(selectors.applyRecipeError);

  const [qrSrc, setQrSrc] = useState<string>('');
  const [qrError, setQrError] = useState<string>('');

  const statusContent = useMemo(() => {
    if (provisionStatusLoading) {
      return (
        <CircularProgress
          size={20}
          color="inherit"
          sx={{ margin: '5px 10px' }}
        />
      );
    }
    if (provisionStatusError) {
      return <p style={{ color: 'red' }}>{t('RXGatePage.PROVISION_FAILED')}</p>;
    }
    if (provisionStatus) {
      return (
        <p style={{ color: 'green' }}>{t('RXGatePage.PROVISION_SUCCESS')}</p>
      );
    }
    return ' ';
  }, [provisionStatus, provisionStatusError]);

  const handleApplyRecipe = () => {
    if (!provisionStatus || !rxGateChosenReceipt) return;
    const currentItems = rxGateDistributeClientBonus(
      rxGateChosenReceipt?.products ?? [],
      0
    );

    dispatch(
      actions.applyRecipe.request({
        postEffect: async () => {
          try {
            setQrError('');
            const payload = JSON.stringify({
              doc_id: rxGateChosenReceipt?.docId,
              items: currentItems?.map((item) => ({
                ware_id: item.ware_id,
                price: item.cost_co,
                quantity: item.quantity,
              })),
            });

            const dataUrl = await QRCode.toDataURL(payload, {
              errorCorrectionLevel: 'M',
              margin: 1,
              scale: 4,
            });

            setQrSrc(dataUrl);
          } catch (e: any) {
            console.error('QR generation error:', e);
            setQrSrc('');
            setQrError(e?.message || 'Не удалось сгенерировать QR');
          }
        },
      })
    );
  };

  const handleCheckProvision = () => {
    dispatch(actions.checkProvision.request());
  };

  return (
    <Styled.ApplyRecipeInfoContainer>
      <Styled.ApplyRecipeInfoBlock>
        <Styled.ApplyProductsButton
          onClick={handleCheckProvision}
          disabled={!!rxGateChosenReceipt?.docErr}
        >
          {t('RXGatePage.CHECK_PROVISION')}
        </Styled.ApplyProductsButton>
        <div className="provision-status">{statusContent}</div>
        {provisionStatus && rxGateChosenReceipt && (
          <Styled.ApplyProductsButton onClick={handleApplyRecipe}>
            {t('RXGatePage.SUBMIT')}
          </Styled.ApplyProductsButton>
        )}
      </Styled.ApplyRecipeInfoBlock>
      <div
        style={{
          minHeight: 80,
          display: 'flex',
          alignItems: 'flex-end',
          alignSelf: 'flex-start',
          justifyContent: 'center',
        }}
      >
        {qrSrc && applyRecipeResult && (
          <img alt="QR" width={120} height={120} src={qrSrc} />
        )}
      </div>
      {qrError && <div className="error">{qrError}</div>}
    </Styled.ApplyRecipeInfoContainer>
  );
};

export default ApplyRecipeInfo;
